import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../auth/AuthContext';
import {
  adminCreateUser, adminListPermissions, adminListUsers, adminSetAccess, adminSetActive, adminSetPassword,
} from '../../api/adminUsers';
import AdminShell, { useAdminAccess } from '../admin/AdminShell';

const roleTabs = [['', 'All accounts'], ['Staff', 'Staff'], ['Admin', 'Administrators'], ['User', 'Customers']];
const roleLabel = { User: 'Customer', Staff: 'Staff', Admin: 'Administrator' };
const roleHelp = {
  User: 'A customer account: books trips and sees My Trips. No admin screens.',
  Staff: 'Uses only the admin screens ticked below.',
  Admin: 'Uses every admin screen, including supplier settings and managing users and staff.',
};

/** Administrators create staff accounts, choose which admin screens each one can use, and manage every account. */
export default function AdminUsersPage() {
  const { auth, user: me } = useAuth();
  const token = auth?.accessToken;
  const hasAccess = useAdminAccess();
  const [role, setRole] = useState('Staff');
  const [search, setSearch] = useState('');
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [editing, setEditing] = useState(null); // { mode: 'create' | 'access' | 'password', account? }
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setData(await adminListUsers(token, { role, search, page, pageSize: 25 }));
    } catch (requestError) {
      setError(requestError.message);
    }
  }, [token, role, search, page]);

  useEffect(() => { if (hasAccess) load(); }, [hasAccess, load]);
  useEffect(() => {
    if (hasAccess) adminListPermissions(token).then(setPermissions).catch((e) => setError(e.message));
  }, [hasAccess, token]);

  const toggleActive = async (account) => {
    setBusyId(account.id); setError(''); setNotice('');
    try {
      await adminSetActive(token, account.id, !account.isActive);
      setNotice(`${account.email} ${account.isActive ? 'deactivated — they are signed out at once' : 'activated'}.`);
      await load();
    } catch (e) { setError(e.message); } finally { setBusyId(null); }
  };

  const saved = (message) => { setEditing(null); setNotice(message); load(); };
  const labelOf = (key) => permissions.find((p) => p.key === key)?.label || key;

  return (
    <AdminShell
      title="Users & staff"
      subtitle="Create staff accounts and choose which admin screens each person can use. Changes apply on their next click; deactivating an account signs it out at once."
      actions={<button type="button" className="btn btn-primary btn-sm" onClick={() => setEditing({ mode: 'create' })}>Add staff member</button>}
    >
      <div className="admin-nav admin-tabs" role="tablist">
        {roleTabs.map(([value, label]) => (
          <button key={label} type="button" role="tab" aria-selected={role === value} className={role === value ? 'active' : ''}
            onClick={() => { setRole(value); setPage(1); }}>{label}</button>
        ))}
      </div>
      <form className="admin-filters" onSubmit={(e) => { e.preventDefault(); setSearch(searchText.trim()); setPage(1); }}>
        <label className="field grow"><span>Search</span>
          <input type="search" placeholder="Name, email or phone" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
        </label>
        <button type="submit" className="btn btn-primary btn-sm">Search</button>
      </form>

      {error && <p className="api-message error" role="alert">{error}</p>}
      {notice && <p className="api-message success" role="status">{notice}</p>}
      {!data && !error && <p role="status">Loading accounts…</p>}
      {data && (
        <>
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Screens</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
              <tbody>
                {data.users.map((account) => (
                  <tr key={account.id}>
                    <td>{account.firstName} {account.lastName}{account.id === me?.id && <span className="muted small"> (you)</span>}</td>
                    <td>{account.email}</td>
                    <td><span className={`status-pill${account.role === 'Admin' ? ' live' : ''}`}>{roleLabel[account.role]}</span></td>
                    <td className="admin-wrap">
                      {account.role === 'Admin' && <span className="muted small">All screens</span>}
                      {account.role === 'Staff' && account.permissions.map(labelOf).join(', ')}
                      {account.role === 'User' && <span className="muted small">—</span>}
                    </td>
                    <td>
                      {account.isActive ? 'Active' : <span className="status-pill cancelled">Inactive</span>}
                      {account.isLockedOut && <><br /><span className="muted small">Locked (wrong passwords)</span></>}
                    </td>
                    <td>{new Date(account.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div className="admin-row-actions">
                        <button className="btn btn-outline btn-sm" onClick={() => setEditing({ mode: 'access', account })}>Access</button>
                        <button className="btn btn-outline btn-sm" onClick={() => setEditing({ mode: 'password', account })}>Password</button>
                        {account.id !== me?.id && (
                          <button className="btn btn-ghost btn-sm" disabled={busyId === account.id} onClick={() => toggleActive(account)}>
                            {account.isActive ? 'Deactivate' : 'Activate'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {!data.users.length && <tr><td colSpan="7">No accounts found.</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="admin-pagination">
            <span>{data.totalCount} account{data.totalCount === 1 ? '' : 's'}</span>
            <button className="btn btn-outline btn-sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>Previous</button>
            <span>Page {page}</span>
            <button className="btn btn-outline btn-sm" disabled={page * data.pageSize >= data.totalCount} onClick={() => setPage(page + 1)}>Next</button>
          </div>
        </>
      )}

      {editing?.mode === 'create' && (
        <AccountDialog token={token} permissions={permissions} onClose={() => setEditing(null)}
          onSaved={(account) => saved(`${account.email} created as ${roleLabel[account.role].toLowerCase()}. Share the password with them securely and ask them to change it.`)} />
      )}
      {editing?.mode === 'access' && (
        <AccountDialog token={token} permissions={permissions} account={editing.account} isSelf={editing.account.id === me?.id}
          onClose={() => setEditing(null)} onSaved={(account) => saved(`Access for ${account.email} saved.`)} />
      )}
      {editing?.mode === 'password' && (
        <PasswordDialog token={token} account={editing.account} onClose={() => setEditing(null)}
          onSaved={(account) => saved(`New password set for ${account.email}.`)} />
      )}
    </AdminShell>
  );
}

/** Creates an account (no `account`) or changes an existing account's role and screens. */
function AccountDialog({ token, permissions, account, isSelf, onClose, onSaved }) {
  const creating = !account;
  const [form, setForm] = useState({
    firstName: '', lastName: '', email: '', password: '',
    role: account?.role || 'Staff',
    permissions: account?.role === 'Staff' ? account.permissions : [],
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const toggle = (key) => setForm((f) => ({
    ...f, permissions: f.permissions.includes(key) ? f.permissions.filter((p) => p !== key) : [...f.permissions, key],
  }));

  const submit = async (event) => {
    event.preventDefault();
    if (form.role === 'Staff' && !form.permissions.length) { setError('Tick at least one screen for a staff account.'); return; }
    setBusy(true); setError('');
    try {
      const perms = form.role === 'Staff' ? form.permissions : [];
      onSaved(creating
        ? await adminCreateUser(token, { ...form, email: form.email.trim(), permissions: perms })
        : await adminSetAccess(token, account.id, form.role, perms));
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <form className="overlay-card modal admin-detail" onClick={(e) => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true"
        aria-label={creating ? 'Add staff member' : 'Change access'}>
        <div className="admin-detail-head">
          <h3>{creating ? 'Add staff member' : `Access for ${account.firstName} ${account.lastName}`}</h3>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose} aria-label="Close">✕</button>
        </div>
        {!creating && <p className="muted small">{account.email}</p>}
        {error && <p className="api-message error" role="alert">{error}</p>}

        {creating && (
          <div className="admin-filters">
            <label className="field"><span>First name</span><input required maxLength="100" value={form.firstName} onChange={set('firstName')} /></label>
            <label className="field"><span>Last name</span><input required maxLength="100" value={form.lastName} onChange={set('lastName')} /></label>
            <label className="field grow"><span>Email (sign-in)</span><input type="email" required maxLength="256" value={form.email} onChange={set('email')} /></label>
            <label className="field grow"><span>Initial password</span>
              <input type="text" required minLength="10" maxLength="128" autoComplete="new-password" value={form.password} onChange={set('password')}
                placeholder="10+ characters, upper & lower case, a number and a symbol" />
            </label>
          </div>
        )}

        <fieldset className="admin-section">
          <legend>Role</legend>
          <div className="admin-role-options">
            {['Staff', 'Admin', 'User'].map((r) => (
              <label key={r} className={`admin-role-option${form.role === r ? ' active' : ''}`}>
                <input type="radio" name="role" value={r} checked={form.role === r} disabled={isSelf && r !== 'Admin'} onChange={set('role')} />
                <span><b>{roleLabel[r]}</b><span className="muted small">{roleHelp[r]}</span></span>
              </label>
            ))}
          </div>
          {isSelf && <p className="muted small">You can't remove your own administrator role.</p>}
        </fieldset>

        {form.role === 'Staff' && (
          <fieldset className="admin-section">
            <legend>Screens this person can use</legend>
            <div className="admin-perm-grid">
              {permissions.map((p) => (
                <label key={p.key} className="admin-perm">
                  <input type="checkbox" checked={form.permissions.includes(p.key)} onChange={() => toggle(p.key)} />
                  <span><b>{p.label}</b><span className="muted small">{p.description}</span></span>
                </label>
              ))}
            </div>
            <p className="muted small">Supplier settings and Users & staff are for administrators only.</p>
          </fieldset>
        )}

        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : creating ? 'Create account' : 'Save access'}</button>
        </div>
      </form>
    </div>
  );
}

function PasswordDialog({ token, account, onClose, onSaved }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    setBusy(true); setError('');
    try { onSaved(await adminSetPassword(token, account.id, password)); } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  return (
    <div className="overlay" onClick={onClose}>
      <form className="overlay-card modal" onClick={(e) => e.stopPropagation()} onSubmit={submit} role="dialog" aria-modal="true" aria-label="Set password">
        <h3>New password for {account.firstName} {account.lastName}</h3>
        <p className="muted small">{account.email} · also unlocks the account if it was locked after wrong passwords.</p>
        {error && <p className="api-message error" role="alert">{error}</p>}
        <label className="field"><span className="small">New password</span>
          <input type="text" required minLength="10" maxLength="128" autoComplete="new-password" value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Set password'}</button>
        </div>
      </form>
    </div>
  );
}
